/**
 * <LiveAutoModeSheet> — le mode autonome d'un cours de groupe : Sam propose la suite (template).
 *
 * L'élève finit une activité en Concentré et sa liste est vide : plutôt que d'attendre son masteur,
 * il dit à Sam comment ça s'est passé, puis choisit l'une des activités que Sam lui prépare. Deux
 * étapes, les mots et les choix de l'ancien mode autonome (`AutoEvalOverlay`, `AutoModeChoiceModal`) :
 *  - **l'auto-évaluation** : « Tout compris ! », « Pas tout compris », « Bloqué, j'ai besoin d'aide » ;
 *  - **le choix** : deux ou trois activités (ce qu'elles sont pour, la matière, la durée, le niveau),
 *    leurs places gardées pendant que Sam les prépare, et « Refaire la même activité ». Rien ne
 *    vient : la phrase de Sam et « Lever la main ».
 *
 * Une feuille basse sur un téléphone, une carte au milieu sur un grand écran (`<Modal>`), comme
 * « Mes activités ». La croix referme : l'élève revient au live.
 *
 * Contrôlée : l'étape, les propositions et les gestes arrivent en props.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Skeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Modal } from "../../molecules/Modal"
import { OptionCard } from "../../molecules/OptionCard"

export type UIAutoModeEvaluation = "got_it" | "mixed" | "lost"

export type UIAutoModeIntent =
    "more_difficult" | "other_concepts" | "same_diversified" | "same_difficulty" | "concept_review" | "method_review"

/** Une activité que Sam propose : ce qu'elle est pour, et sa ligne (matière, durée, niveau). */
export type UIAutoModeProposal = { id: string; intent: UIAutoModeIntent; detail?: string }

export type LiveAutoModeSheetProps = {
    visible: boolean
    onClose: () => void
    step: "evaluate" | "choose"
    onEvaluate: (evaluation: UIAutoModeEvaluation) => void
    /** La réponse donnée, qui dit la phrase de Sam au choix. */
    evaluation?: UIAutoModeEvaluation
    proposals?: UIAutoModeProposal[]
    /** Sam prépare : les places des propositions à venir. Défaut 0. */
    pendingCount?: number
    /** Rien n'a pu être préparé : la phrase et « Lever la main ». */
    failed?: "unavailable" | "generation" | null
    onChoose?: (proposal: UIAutoModeProposal) => void
    /** « Refaire la même activité » ; `null` : l'activité ne se relance pas (sans support). */
    onReplay?: (() => void) | null
    /** Le choix part : la proposition choisie, ou `replay`. */
    starting?: string | null
    /** Le choix n'est pas parti. Défaut `false`. */
    startFailed?: boolean
    onRaiseHand?: () => void
    testID?: string
}

const EVALUATIONS: { key: UIAutoModeEvaluation; label: string; icon: IconName; color: SemanticColorKey }[] = [
    { key: "got_it", label: "Tout compris !", icon: "check-circle", color: "success" },
    { key: "mixed", label: "Pas tout compris", icon: "question-circle", color: "warning" },
    { key: "lost", label: "Bloqué, j'ai besoin d'aide", icon: "times-circle", color: "danger" },
]

/** Ce que chaque activité proposée est pour : les mots de l'ancien choix. */
const INTENTS: Record<UIAutoModeIntent, { label: string; icon: IconName }> = {
    more_difficult: { label: "Un défi plus dur", icon: "fire" },
    other_concepts: { label: "Une autre notion de la leçon", icon: "exchange-alt" },
    same_diversified: { label: "Un exercice différent, même niveau", icon: "magic" },
    same_difficulty: { label: "Un exercice du même genre", icon: "redo" },
    concept_review: { label: "Revoir la leçon", icon: "lightbulb" },
    method_review: { label: "Revoir la méthode", icon: "graduation-cap" },
}

const TEXTS = {
    title: "Avec Sam",
    evaluate: "Cette activité, comment ça s'est passé ?",
    preparing: "Je te prépare tes activités. Une seconde…",
    chooseLost: "On reprend les bases ensemble. Choisis ta prochaine activité.",
    chooseGotIt: "Bravo ! Voici des activités plus poussées. Choisis celle qui te tente.",
    chooseMixed: "Encore quelques variations pour bien ancrer. Choisis ta prochaine.",
    unavailable: "Impossible de générer des suggestions pour cette activité.",
    generation: "Désolé, je n'arrive pas à te proposer une activité.",
    raiseHand: "Lever la main",
    replay: "Refaire la même activité",
    replayUnavailable: "Cette activité ne peut pas être relancée",
    startFailed: "Impossible de lancer cette activité. Réessaie.",
}

function samLine(props: LiveAutoModeSheetProps): string {
    const { step, evaluation, failed, proposals = [], pendingCount = 0 } = props
    if (step === "evaluate") return TEXTS.evaluate
    if (failed != null && proposals.length === 0) return failed === "unavailable" ? TEXTS.unavailable : TEXTS.generation
    if (pendingCount > 0 && proposals.length === 0) return TEXTS.preparing
    return evaluation === "lost" ? TEXTS.chooseLost : evaluation === "got_it" ? TEXTS.chooseGotIt : TEXTS.chooseMixed
}

function Leading({ icon, color = "primary" }: { icon: IconName; color?: SemanticColorKey }) {
    return <Icon name={icon} size={22} color={color} />
}

export function LiveAutoModeSheet(props: LiveAutoModeSheetProps) {
    const {
        visible,
        onClose,
        step,
        onEvaluate,
        proposals = [],
        pendingCount = 0,
        failed = null,
        onChoose,
        onReplay,
        starting = null,
        startFailed = false,
        onRaiseHand,
        testID,
    } = props
    const busy = starting != null

    return (
        <Modal visible={visible} onClose={onClose} title={TEXTS.title} sizeToContent testID={testID}>
            <Stack gap="4">
                <Row gap="3" align="center">
                    <ChampionAvatar name="sam" size={44} />
                    <Text size="text-md" weight="semibold" color="text-strong" style={styles.line}>
                        {samLine(props)}
                    </Text>
                </Row>

                {step === "evaluate" ? (
                    <Stack gap="2">
                        {EVALUATIONS.map((item) => (
                            <OptionCard
                                key={item.key}
                                label={item.label}
                                selected={false}
                                leading={<Leading icon={item.icon} color={item.color} />}
                                onPress={() => onEvaluate(item.key)}
                            />
                        ))}
                    </Stack>
                ) : failed != null && proposals.length === 0 ? (
                    <View style={styles.action}>
                        {onRaiseHand != null && (
                            <Button label={TEXTS.raiseHand} iconName="hand-paper" onPress={onRaiseHand} />
                        )}
                    </View>
                ) : (
                    <Stack gap="2">
                        {proposals.map((proposal) => (
                            <OptionCard
                                key={proposal.id}
                                label={INTENTS[proposal.intent].label}
                                description={proposal.detail}
                                selected={starting === proposal.id}
                                disabled={busy && starting !== proposal.id}
                                leading={<Leading icon={INTENTS[proposal.intent].icon} />}
                                onPress={() => onChoose?.(proposal)}
                            />
                        ))}
                        {Array.from({ length: pendingCount }, (_, index) => (
                            <Skeleton key={`pending-${index}`} shape="block" height={72} />
                        ))}
                        <OptionCard
                            label={TEXTS.replay}
                            selected={starting === "replay"}
                            disabled={onReplay == null || (busy && starting !== "replay")}
                            disabledReason={onReplay == null ? TEXTS.replayUnavailable : undefined}
                            leading={<Leading icon="undo-alt" />}
                            onPress={() => onReplay?.()}
                        />
                        {startFailed && (
                            <Text size="text-sm" color="danger">
                                {TEXTS.startFailed}
                            </Text>
                        )}
                    </Stack>
                )}
            </Stack>
        </Modal>
    )
}

const styles = StyleSheet.create((theme) => ({
    line: { flex: 1, flexShrink: 1 },
    /** Le bouton seul sous la phrase de Sam : centré, comme les autres boutons seuls d'une feuille. */
    action: { alignItems: "center", paddingTop: theme.spacing["1"] },
}))
